import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { QRCodeSVG } from 'qrcode.react';
import '../styles/Wallets.css';
import Loader from '../components/Loader';
import { FaUserCircle } from "react-icons/fa";

const Wallets = () => {
    const navigate = useNavigate();
    const [wallets, setWallets] = useState({});
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState({});
    const [tempValues, setTempValues] = useState({});
    const [savingWallet, setSavingWallet] = useState(null);
    const [deletingWallet, setDeletingWallet] = useState(null);
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newWallet, setNewWallet] = useState({ name: '', address: '' });
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        const fetchCurrentUserRole = async () => {
            const auth = getAuth();
            const currentUser = auth.currentUser;

            if (!currentUser) {
                setCurrentUserRole("unauthenticated");
                setAuthLoading(false);
                return;
            }

            try {
                const currentUserDoc = await getDoc(doc(db, "users", currentUser.uid));
                if (currentUserDoc.exists()) {
                    const role = currentUserDoc.data().role || "user";
                    setCurrentUserRole(role);
                } else {
                    setCurrentUserRole("unknown");
                }
            } catch (error) {
                console.error("Error fetching current user role:", error);
                setCurrentUserRole("error");
            } finally {
                setAuthLoading(false);
            }
        };

        fetchCurrentUserRole();
    }, []);

    useEffect(() => {
        const fetchWalletData = async () => {
            try {
                const docRef = doc(db, 'users', 'wallets');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setWallets(data);
                    setTempValues(data);
                } else {
                    console.error("No wallets document found!");
                }
            } catch (err) {
                console.error("Error fetching wallet data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchWalletData();
    }, []);

    const toggleEdit = (key) => {
        setEditMode(prev => ({ ...prev, [key]: !prev[key] }));
        // Reset tempValues for this wallet when toggling edit off
        if (editMode[key]) {
            setTempValues(prev => ({
                ...prev,
                [key]: wallets[key]
            }));
        }
    };

    const handleChange = (e, key, field) => {
        setTempValues(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: e.target.value
            }
        }));
    };

    const saveAddress = async (key) => {
        setSavingWallet(key);
        try {
            const updated = {
                ...wallets,
                [key]: {
                    ...wallets[key],
                    name: tempValues[key].name,
                    address: tempValues[key].address,
                }
            };

            await updateDoc(doc(db, 'users', 'wallets'), updated);
            setWallets(updated);
            toggleEdit(key);
        } catch (error) {
            console.error('Error updating wallet:', error);
        } finally {
            setSavingWallet(null);
        }
    };

    const deleteWallet = async (key) => {
        setDeletingWallet(key);
        try {
            const walletDocRef = doc(db, 'users', 'wallets');

            // Remove key from Firestore
            await updateDoc(walletDocRef, {
                [key]: deleteField(),
            });

            // Remove key from local state
            const updatedWallets = { ...wallets };
            delete updatedWallets[key];
            setWallets(updatedWallets);
            setTempValues(updatedWallets);

            setEditMode(prev => {
                const copy = { ...prev };
                delete copy[key];
                return copy;
            });
        } catch (error) {
            console.error('Error deleting wallet:', error);
        } finally {
            setDeletingWallet(null);
        }
    };



    const handleGoBack = () => {
        navigate(-1);
    };

    const handleCreateWallet = async () => {
        const trimmedName = newWallet.name.trim().toLowerCase();
        const address = newWallet.address.trim();

        if (!trimmedName || !address) return;

        setCreating(true);
        try {
            const walletNumbers = Object.keys(wallets)
                .map(key => {
                    const match = key.match(/^wallet(\d+)$/);
                    return match ? parseInt(match[1], 10) : 0;
                })
                .filter(num => num > 0);

            const nextNumber = walletNumbers.length > 0 ? Math.max(...walletNumbers) + 1 : 1;
            const newKey = `wallet${nextNumber}`;

            const newWalletEntry = {
                ...wallets,
                [newKey]: {
                    name: trimmedName,
                    address,
                }
            };

            await updateDoc(doc(db, 'users', 'wallets'), newWalletEntry);
            setWallets(newWalletEntry);
            setTempValues(newWalletEntry);
            setNewWallet({ name: '', address: '' });
            setShowCreateForm(false);
        } catch (error) {
            console.error('Error creating new wallet:', error);
        } finally {
            setCreating(false);
        }
    };

    if (loading || authLoading) return <Loader />;

    if (currentUserRole !== "admin") {
        return (
            <div className="web3-user-not-found">
                <FaUserCircle size={48} />
                <h2>Access Denied</h2>
                <div onClick={handleGoBack} className="route-Links">
                    Go Back
                </div>
            </div>
        );
    }

    const sortedWalletEntries = Object.entries(wallets)
        .filter(([key]) => /^wallet\d+$/.test(key))
        .sort((a, b) => {
            const aNum = parseInt(a[0].replace('wallet', ''), 10);
            const bNum = parseInt(b[0].replace('wallet', ''), 10);
            return aNum - bNum;
        });

    return (
        <div className="wallets-container">
            <h2>Wallet Addresses</h2>

            {/* Create Wallet Section */}
            <div>
                {showCreateForm ? (
                    <div
                        className="modal-overlay"
                        onClick={() => setShowCreateForm(false)}
                    >
                        <div
                            className="plan-create-form"
                            onClick={(e) => e.stopPropagation()}
                        >
                    <div className="plan-modal-body">
                                <div><h2>Add a New Wallet</h2></div>
                                <div>
                                    <label>Enter Wallet Name</label>
                                    <input
                                        className="input-field"
                                        type="text"
                                        placeholder="Wallet Name (e.g. bitcoin)"
                                        value={newWallet.name}
                                        onChange={(e) => setNewWallet({ ...newWallet, name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label>Enter Wallet Address</label>
                                    <input
                                        className="input-field"
                                        type="text"
                                        placeholder="eg. xyzBTCwallet"
                                        value={newWallet.address}
                                        onChange={(e) => setNewWallet({ ...newWallet, address: e.target.value })}
                                    />
                                </div>
                                <div className="wallet-actions">
                                    <button className='action-btn trade' onClick={handleCreateWallet} disabled={creating}>
                                        {creating ? "Creating..." : "Create"}
                                    </button>
                                    <button
                                        className='action-btn withdraw'
                                        onClick={() => setShowCreateForm(false)}
                                        disabled={creating}
                                    >
                                        Cancel
                                    </button>
                                </div>
                    </div>
                        </div>
                    </div>
                ) : (
                    <button
                        className="create-btn"
                        onClick={() => setShowCreateForm(true)}
                        style={{ marginTop: "10px" }}
                    >
                        + Add New Wallet
                    </button>
                )}
            </div>


            {sortedWalletEntries.map(([key, { name, address }]) => (
                <div key={key} className="wallet-box">
                    <div className="wallet-top-row">
                        <div className="wallet-qr">
                            <QRCodeSVG
                                value={tempValues[key]?.address || address}
                                size={150}
                                level="H"
                            />
                        </div>
                        <div className="wallet-right">
                            <div className="wallet-header">
                                <h3>{name.toUpperCase()}</h3>
                            </div>

                            {editMode[key] ? (
                                <>
                                    <label>Wallet Name</label>
                                    <input
                                        className="input-field"
                                        type="text"
                                        value={tempValues[key]?.name || ''}
                                        onChange={(e) => handleChange(e, key, 'name')}
                                        placeholder="Wallet Name"
                                    />
                                    <label>Wallet Address</label>
                                    <input
                                        className="input-field"
                                        type="text"
                                        value={tempValues[key]?.address || ''}
                                        onChange={(e) => handleChange(e, key, 'address')}
                                        placeholder="Wallet Address"
                                    />
                                    <div className="plan-actions">
                                        <button
                                            className='action-btn trade'
                                            onClick={() => saveAddress(key)}
                                            disabled={savingWallet === key}
                                        >
                                            {savingWallet === key ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                            className='action-btn withdraw'
                                            onClick={() => toggleEdit(key)}
                                            disabled={savingWallet === key}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="wallet-address">{address}</p>
                                    <div className="plan-actions">
                                        <button
                                            // className='action-btn deposit'
                                            onClick={() => toggleEdit(key)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className='action-btn withdraw'
                                            onClick={() => deleteWallet(key)}
                                            disabled={deletingWallet === key}
                                        >
                                            {deletingWallet === key ? 'Deleting...' : 'Delete'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            ))}

        </div>
    );
};

export default Wallets;
